import React from 'react';
import { motion } from 'framer-motion';
interface VerbTableProps {
  verb: string;
  tense: 'present' | 'past' | 'perfect';
  persons: string[];
  forms: string[];
  highlight?: string;
  label?: string;
  /** 'stimulus' = compact visual only (shown in-question), 'support' = full educational panel (shown after answer) */
  mode?: 'stimulus' | 'support';
}
/**
 * VerbTable - Dutch verb conjugation table visual
 *
 * Shows verb conjugation patterns with educational explanations.
 * Used for SWW (Spelling Werkwoorden) module questions.
 *
 * stimulus mode: compact — verb info + conjugation table only
 * support mode: full educational panel with explanations and tips
 */
export function VerbTable({
  verb,
  tense,
  persons,
  forms,
  highlight,
  label,
  mode = 'support'
}: VerbTableProps) {
  // Extract stem (remove -en ending)
  const stem = verb.endsWith('en') ? verb.slice(0, -2) : verb;
  // Tense labels
  const tenseLabels = {
    present: 'Tegenwoordige Tijd',
    past: 'Verleden Tijd',
    perfect: 'Voltooid Deelwoord'
  };
  // ── STIMULUS MODE: compact — verb + table only ──────────────────────────
  if (mode === 'stimulus') {
    return <div className="w-full max-w-md mx-auto" data-id="element-14663" data-mp="1rneadh1cnb8">
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200" data-id="element-14664" data-mp="6nua3dfpwifg">
          {/* Header - compact */}
          {label && <div className="mb-3 text-center" data-id="element-14665" data-mp="fplfsia9cp9b">
              <span className="text-xs font-bold text-indigo-800" data-id="element-14666" data-mp="fosh84lgd629">{label}</span>
            </div>}

          {/* Verb Info - compact */}
          <div className="mb-3 flex items-center justify-center gap-3" data-id="element-14667" data-mp="kmll2qza9nzd">
            <div className="text-center" data-id="element-14668" data-mp="pfwvt377uowk">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-14669" data-mp="uehvn1ik36kh">
                Werkwoord
              </div>
              <div className="text-lg font-black text-indigo-700" data-id="element-14670" data-mp="yguposnxxg1f">{verb}</div>
            </div>
            <div className="text-lg text-slate-300" data-id="element-14671" data-mp="13emtol1v0slz">→</div>
            <div className="text-center" data-id="element-14672" data-mp="13d3e6wo12ll9">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-14673" data-mp="2zhj5i11gpph4">
                Stam
              </div>
              <div className="text-lg font-black text-purple-700" data-id="element-14674" data-mp="2vi0kl59n5eea">{stem}</div>
            </div>
          </div>

          {/* Conjugation Table - compact */}
          <div className="bg-white/90 rounded-lg overflow-hidden border border-indigo-200" data-id="element-14675" data-mp="34hdtnwtus0yh">
            <table className="w-full text-sm" data-id="element-14676" data-mp="3j6a23uztnfzm">
              <thead data-id="element-14677" data-mp="3j4yxjsyjy47g">
                <tr className="bg-indigo-100/70" data-id="element-14678" data-mp="3j4gvgfp7kvrm">
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-14679" data-mp="3e7qhbxwysa8u">
                    Persoon
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-14680" data-mp="3s6ge1cfvg7wo">
                    Vorm
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-14681" data-mp="1xu0bomq75dx3">
                {persons.map((person, index) => {
                const form = forms[index];
                const isHighlighted = highlight === person || highlight === form;
                return <motion.tr key={person} initial={{
                  opacity: 0,
                  x: -5
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.06
                }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : ''}`} data-id="element-14682" data-mp="8d4n8galee03" data-mpi={index}>
                      
                      <td className="px-3 py-1.5" data-id="element-14683" data-mp="3lpye3i8t3ee2">
                        <span className={`font-semibold text-xs ${isHighlighted ? 'text-indigo-700' : 'text-slate-600'}`} data-id="element-14684" data-mp="3qpjbho8bveoo">
                          
                          {person}
                        </span>
                      </td>
                      <td className="px-3 py-1.5" data-id="element-14685" data-mp="3gsem7fj6r33n">
                        <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-14686" data-mp="1ayoof2nkkrs8">
                          
                          {form}
                        </span>
                      </td>
                    </motion.tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full educational panel (original behavior) ────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-14687" data-mp="gaev4mvwqurx">
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 md:p-8 border-2 border-indigo-200 shadow-lg" data-id="element-14688" data-mp="gd9f6db0edkt">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-14689" data-mp="7bjml45e58lg">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-indigo-200 shadow-sm" data-id="element-14690" data-mp="7cl0dgdsk96v">
            <span className="text-2xl" data-id="element-14691" data-mp="3tptalc5eselp">📝</span>
            <div className="text-left" data-id="element-14692" data-mp="3tqm8dnme3trp">
              <div className="text-sm font-bold text-indigo-900" data-id="element-14693" data-mp="3ori0d0b7c22y">
                {label || 'Werkwoord Vervoegen'}
              </div>
              <div className="text-xs text-indigo-600 font-semibold" data-id="element-14694" data-mp="3jqvbxge9r4oz">
                {tenseLabels[tense]}
              </div>
            </div>
          </div>
        </div>

        {/* Verb Info */}
        <div className="mb-6 bg-white/80 rounded-xl p-4 border border-indigo-100" data-id="element-14695" data-mp="1v7r4uufbzzue">
          <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-14696" data-mp="1z9cp50u73gem">
            <div className="text-center" data-id="element-14697" data-mp="249gx22h1f2t8">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-14698" data-mp="24aigovc4cxmk">
                Werkwoord
              </div>
              <div className="text-2xl font-black text-indigo-700" data-id="element-14699" data-mp="1bkjq2via0sfs">{verb}</div>
            </div>
            <div className="text-2xl text-slate-300" data-id="element-14700" data-mp="1bip3zdv9qua4">→</div>
            <div className="text-center" data-id="element-14701" data-mp="1gkeduq6r91wm">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-14702" data-mp="1lipq17kivoi0">
                Stam
              </div>
              <div className="text-2xl font-black text-purple-700" data-id="element-14703" data-mp="1q9pq2loxumoq">{stem}</div>
            </div>
          </div>
        </div>

        {/* Conjugation Table */}
        <div className="bg-white/90 rounded-xl overflow-hidden border border-indigo-200 shadow-sm mb-6" data-id="element-14704" data-mp="ss7my6jstn4l">
          <table className="w-full" data-id="element-14705" data-mp="psfm4cw87vbs">
            <thead data-id="element-14706" data-mp="pre1bgperugv">
              <tr className="bg-indigo-100 border-b border-indigo-200" data-id="element-14707" data-mp="pqw7sru4rucn">
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-14708" data-mp="lpsrk4867waw">
                  Persoon
                </th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-14709" data-mp="zhh1c0uy68op">
                  Vorm
                </th>
              </tr>
            </thead>
            <tbody data-id="element-14710" data-mp="6umit9hoxjdv">
              {persons.map((person, index) => {
              const form = forms[index];
              const isHighlighted = highlight === person || highlight === form;
              return <motion.tr key={person} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : 'hover:bg-slate-50'}`} data-id="element-14711" data-mp="gsskbpzl4ni7" data-mpi={index}>
                    
                    <td className="px-4 py-3" data-id="element-14712" data-mp="354eqvavsg4vw">
                      <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-700'}`} data-id="element-14713" data-mp="357susnk3om5g">
                        
                        {person}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-14714" data-mp="313l2yhiciq30">
                      <span className={`font-bold text-lg ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-14715" data-mp="314edzuoc81qy">
                        
                        {form}
                      </span>
                    </td>
                  </motion.tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-14716" data-mp="3k2757zplmn8i">
          {/* How to find the stem */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-14717" data-mp="3k0e04oumwb2m">
            <div className="flex items-start gap-3" data-id="element-14718" data-mp="bkuas0x33csu">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-14719" data-mp="geozjirjqhys">
                <span className="text-blue-600 text-lg" data-id="element-14720" data-mp="2chjz15a1wh0h">🔍</span>
              </div>
              <div data-id="element-14721" data-mp="27p0lg8bd5lvo">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-14722" data-mp="27oh4ouxg93j8">
                  Hoe vind je de stam?
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-14723" data-mp="22omb2jmahzuh">
                  De <strong data-id="element-14724" data-mp="1xtp5jmo7y8mc">stam</strong> krijg je door <strong data-id="element-14725" data-mp="2vfmfgvoj95mo">-en</strong>{' '}
                  weg te halen van het hele werkwoord.{' '}
                  <span className="font-mono bg-blue-50 px-2 py-0.5 rounded border border-blue-200" data-id="element-14726" data-mp="2qgt2r5h2link">
                    {verb}
                  </span>{' '}
                  →{' '}
                  <span className="font-mono bg-purple-50 px-2 py-0.5 rounded border border-purple-200" data-id="element-14727" data-mp="2ljb5wdgbutvx">
                    {stem}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Conjugation pattern */}
          {tense === 'present' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-14728" data-mp="162stgo9zg804">
              <div className="flex items-start gap-3" data-id="element-14729" data-mp="3hlyr18wb415e">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-14730" data-mp="3lqolquf8b9oq">
                  <span className="text-emerald-600 text-lg" data-id="element-14731" data-mp="3qn6ndlf9irwu">📋</span>
                </div>
                <div data-id="element-14732" data-mp="3vg83fwr7gxi0">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-14733" data-mp="3vgrequmconn2">
                    Patroon Tegenwoordige Tijd
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-14734" data-mp="97qdn82cvgxt">
                    <p data-id="element-14735" data-mp="da37ay3tvoe8">
                      <strong data-id="element-14736" data-mp="d9lfcdo77wc4">ik:</strong> stam (geen -t) →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-14737" data-mp="i8xzurrlkrbj">
                        {stem}
                      </span>
                    </p>
                    <p data-id="element-14738" data-mp="2f2hw8h4sht45">
                      <strong data-id="element-14739" data-mp="2f3az370mnyh0">jij/je, u:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-14740" data-mp="2k12o9t9w16hi">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-14741" data-mp="cog1n82st3bu">
                      <strong data-id="element-14742" data-mp="cnmq7tsw9pnd">hij/zij/het:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-14743" data-mp="hniu7t1ldh2x">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-14744" data-mp="mndnu4cr4kro">
                      <strong data-id="element-14745" data-mp="mkj1u8e3arw5">wij/jullie/zij:</strong> hele werkwoord →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-14746" data-mp="qno2e5em0ji5">
                        {verb}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {tense === 'past' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-14747" data-mp="15ctvrcqrnfi4">
              <div className="flex items-start gap-3" data-id="element-14748" data-mp="1acg8zm6lgvqx">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-14749" data-mp="1ae153g8k4mqw">
                  <span className="text-emerald-600 text-lg" data-id="element-14750" data-mp="1ebfbz18rmifq">📋</span>
                </div>
                <div data-id="element-14751" data-mp="2ytpwoy9wsgjj">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-14752" data-mp="2uuh2xtevcinz">
                    Patroon Verleden Tijd (Zwakke werkwoorden)
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-14753" data-mp="2pve9l68940vu">
                    <p data-id="element-14754" data-mp="2kxcn0bbm9jfm">
                      <strong data-id="element-14755" data-mp="2ky5qn3fa3stj">enkelvoud:</strong> stam + te / de
                    </p>
                    <p data-id="element-14756" data-mp="3ij0au7u0atfj">
                      <strong data-id="element-14757" data-mp="3ilmligc71p1z">meervoud:</strong> stam + ten / den
                    </p>
                    <p className="mt-2 text-xs text-emerald-800 bg-emerald-100/50 p-2 rounded" data-id="element-14758" data-mp="3dmtcb03xwk7m">
                      <em data-id="element-14759" data-mp="38rx9r612ueg9">
                        Tip: Gebruik 't kofschip om te bepalen of het -te of -de
                        is!
                      </em>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {tense === 'perfect' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-14760" data-mp="cvqs7jwlas4">
              <div className="flex items-start gap-3" data-id="element-14761" data-mp="1umu3q7pfgtyg">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-14762" data-mp="1ynvx47wnybsi">
                  <span className="text-emerald-600 text-lg" data-id="element-14763" data-mp="1kvfz5limd3yg">📋</span>
                </div>
                <div data-id="element-14764" data-mp="1puahyngl6sik">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-14765" data-mp="1ptr6nplfz2di">
                    Patroon Voltooid Deelwoord
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-14766" data-mp="1fwwerpjoc087">
                    <p data-id="element-14767" data-mp="11xd5uuj1gkuu">
                      <strong data-id="element-14768" data-mp="11xwral7wdrdm">Zwakke werkwoorden:</strong> ge + stam + t / d
                    </p>
                    <p data-id="element-14769" data-mp="16vy9l3xi1mo1">
                      <strong data-id="element-14770" data-mp="16x9bey7dwkci">Sterke werkwoorden:</strong> ge + ... + en
                    </p>
                    <p className="mt-2 text-xs text-emerald-800 bg-emerald-100/50 p-2 rounded" data-id="element-14771" data-mp="2wcpim81qzvzr">
                      <em data-id="element-14772" data-mp="319fuej6is5fd">
                        Tip: Maak het woord langer om te horen of het op een -t
                        of -d eindigt (bijv. gemaakte → gemaakt).
                      </em>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-14773" data-mp="3k80z5qfkonw2">
            <div className="flex items-start gap-3" data-id="element-14774" data-mp="3feg5gal0ig2o">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-14775" data-mp="3u4e11bl9u81w">
                <span className="text-amber-600 text-lg" data-id="element-14776" data-mp="3p6m23xm7llo7">💡</span>
              </div>
              <div data-id="element-14777" data-mp="70pq9bxaak5n">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-14778" data-mp="6z6bjpra0h5z">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-14779" data-mp="2z4anitd7rrr">
                  Bij <strong data-id="element-14780" data-mp="28sm10jbmnpzd">inversie</strong> (als 'jij' achter het werkwoord
                  staat) valt de -t weg: <em data-id="element-14781" data-mp="24r0sfa64lxuz">jij loopt</em> wordt{' '}
                  <em data-id="element-14782" data-mp="24sbvt8vnt3lk">loop jij?</em>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}